import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Sparkles, Send, Loader2, ImagePlus, X, Wand2, Check, Mic, MicOff } from "lucide-react";
import { useSpeechRecognition } from "../../hooks/useSpeechRecognition";
import { Dialog, DialogContent, DialogTitle } from "../ui/dialog";
import { api, formatApiErrorDetail } from "../../lib/api";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const EXAMPLES = [
  "Add a new category 'Best Fuel Credit Cards' with two great cashback fuel cards.",
  "Change the accent color to a warm gold and update my bio to be more punchy.",
  "Add a product: Ridge Wallet in the lifestyle collection with a 10% discount code ALY10.",
];

const ACTION_LABEL = {
  update_settings: "Update settings",
  add_category: "Add category",
  update_category: "Rename category",
  delete_category: "Delete category",
  add_card: "Add credit card",
  update_card: "Update card",
  delete_card: "Delete card",
  add_product: "Add product",
  update_product: "Update product",
  delete_product: "Delete product",
};

function opSummary(op) {
  const d = op.data || {};
  const label = ACTION_LABEL[op.action] || op.action;
  const detail = d.card_name || d.name || d.category || d.collection_key || (d.name) || (op.action === "update_settings" ? Object.keys(d).join(", ") : "") || "";
  return { label, detail };
}

export default function AdminAI({ open, onClose }) {
  const [messages, setMessages] = useState([]);
  const [prompt, setPrompt] = useState("");
  const [images, setImages] = useState([]);
  const [busy, setBusy] = useState(false);
  const [applying, setApplying] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [plan, setPlan] = useState(null); // {message, operations}
  const [dragOver, setDragOver] = useState(false);
  const fileRef = useRef(null);
  const scrollRef = useRef(null);
  const dragDepth = useRef(0);
  const voiceBaseRef = useRef("");

  // Voice-to-text: preserve any typed prompt before dictation started, then
  // append the transcript live so users see words appear as they speak.
  const voice = useSpeechRecognition({
    onTranscript: ({ text, isFinal }) => {
      const base = voiceBaseRef.current;
      const joined = base ? `${base.trim()} ${text}`.replace(/\s+/g, " ") : text;
      setPrompt(joined);
      if (isFinal) {
        // Lock the final chunk into the base so future interim doesn't erase it.
        voiceBaseRef.current = joined;
      }
    },
  });

  const toggleVoice = () => {
    if (voice.listening) {
      // Turning mic OFF — auto-send whatever the user just dictated. This makes
      // the flow feel truly hands-free: mic-on → speak → mic-off → plan appears.
      voice.stop();
      const p = (prompt || "").trim();
      if (p && !busy) setTimeout(() => send(p), 120);
    } else {
      voiceBaseRef.current = prompt || "";
      voice.start();
    }
  };
  useEffect(() => { if (voice.error) toast.error(voice.error); }, [voice.error]);

  const scrollDown = () => setTimeout(() => scrollRef.current?.scrollTo(0, scrollRef.current.scrollHeight), 50);

  const uploadFiles = async (fileList) => {
    const files = Array.from(fileList || []).filter((f) => f.type.startsWith("image/"));
    if (!files.length) return;
    setUploading(true);
    try {
      const results = await Promise.all(files.map(async (f) => {
        const fd = new FormData();
        fd.append("file", f);
        const { data } = await api.post("/admin/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        return { url: data.url, path: data.path };
      }));
      setImages((prev) => [...prev, ...results]);
      toast.success(`${results.length} image${results.length > 1 ? "s" : ""} attached`);
    } catch { toast.error("Image upload failed"); } finally { setUploading(false); }
  };

  const attach = (e) => uploadFiles(e.target.files);

  const onDragEnter = (e) => {
    if (!e.dataTransfer?.types?.includes("Files")) return;
    e.preventDefault();
    dragDepth.current += 1;
    setDragOver(true);
  };
  const onDragOver = (e) => {
    if (e.dataTransfer?.types?.includes("Files")) e.preventDefault();
  };
  const onDragLeave = () => {
    dragDepth.current = Math.max(0, dragDepth.current - 1);
    if (dragDepth.current === 0) setDragOver(false);
  };
  const onDrop = (e) => {
    if (!e.dataTransfer?.files?.length) return;
    e.preventDefault();
    dragDepth.current = 0;
    setDragOver(false);
    uploadFiles(e.dataTransfer.files);
  };
  const onPaste = (e) => {
    const items = Array.from(e.clipboardData?.items || []).filter((it) => it.kind === "file" && it.type.startsWith("image/"));
    if (!items.length) return;
    e.preventDefault();
    uploadFiles(items.map((it) => it.getAsFile()).filter(Boolean));
  };

  const send = async (text) => {
    const p = (text ?? prompt).trim();
    if (!p || busy) return;
    const attached = images;
    setMessages((m) => [...m, { role: "user", text: p }]);
    setPrompt(""); setImages([]); setPlan(null); setBusy(true);
    voiceBaseRef.current = "";
    scrollDown();
    try {
      const { data } = await api.post("/admin/ai/plan", {
        prompt: p,
        image_paths: attached.map((i) => i.path),
        image_urls: attached.map((i) => `${BACKEND_URL}${i.url}`),
      });
      setMessages((m) => [...m, { role: "assistant", text: data.message }]);
      setPlan(data);
    } catch (e) {
      // Restore the prompt + attached images so the owner doesn't lose their work.
      setPrompt(p);
      voiceBaseRef.current = p;
      setImages(attached);
      setMessages((m) => [...m, { role: "assistant", text: `${formatApiErrorDetail(e.response?.data?.detail)} — I kept your prompt and images so you can retry.`, error: true }]);
    } finally { setBusy(false); scrollDown(); }
  };

  const applyPlan = async () => {
    if (!plan?.operations?.length) return;
    setApplying(true);
    try {
      await api.post("/admin/ai/apply", { operations: plan.operations });
      toast.success("Changes applied — reloading to show them");
      setTimeout(() => window.location.reload(), 600);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
      setApplying(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent
        className="flex h-[82vh] max-w-lg flex-col overflow-hidden bg-white p-0 text-[#1d1d1b]"
        aria-describedby={undefined}
        onDragEnter={onDragEnter}
        onDragOver={onDragOver}
        onDragLeave={onDragLeave}
        onDrop={onDrop}
        onPaste={onPaste}
      >
        <DialogTitle className="sr-only">AI Assistant</DialogTitle>
        {dragOver && (
          <div className="pointer-events-none absolute inset-0 z-40 m-2 flex items-center justify-center rounded-2xl border-2 border-dashed border-fuchsia-500 bg-fuchsia-500/10 backdrop-blur-sm">
            <div className="text-center">
              <ImagePlus size={30} className="mx-auto text-fuchsia-600" />
              <p className="mt-1 text-sm font-bold text-fuchsia-700">Drop images to attach</p>
            </div>
          </div>
        )}
        <div className="flex items-center gap-2 border-b border-black/10 px-5 py-4">
          <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-gradient-to-br from-violet-500 to-fuchsia-500 text-white"><Sparkles size={16} /></span>
          <div>
            <h2 className="text-sm font-bold">AI Assistant</h2>
            <p className="text-[11px] text-black/40">Describe changes — review, then apply</p>
          </div>
        </div>

        <div ref={scrollRef} className="flex-1 space-y-4 overflow-y-auto px-5 py-4">
          {messages.length === 0 ? (
            <div className="pt-6 text-center">
              <span className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-2xl bg-gradient-to-br from-violet-500/20 to-fuchsia-500/20"><Wand2 size={22} className="text-fuchsia-500" /></span>
              <p className="text-sm font-semibold">Edit your site with a sentence</p>
              <p className="mx-auto mt-1 max-w-xs text-xs text-black/40">Add cards &amp; products, rename categories, tweak colors or your bio. I&apos;ll show a plan before changing anything.</p>
              <div className="mt-5 space-y-2 text-left">
                {EXAMPLES.map((ex, i) => (
                  <button key={i} onClick={() => send(ex)} data-testid={`admin-ai-example-${i}`} className="w-full rounded-xl border border-black/10 bg-black/[0.02] p-3 text-left text-xs text-black/70 transition-colors hover:border-fuchsia-400/50 hover:bg-black/[0.04]">
                    {ex}
                  </button>
                ))}
              </div>
            </div>
          ) : (
            messages.map((m, i) => (
              <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                <div className={`max-w-[85%] rounded-2xl px-3.5 py-2.5 text-sm ${m.role === "user" ? "bg-[#1d1d1b] text-white" : m.error ? "bg-red-50 text-red-600" : "bg-black/[0.05] text-black/90"}`}>
                  {m.text}
                </div>
              </div>
            ))
          )}

          {plan?.operations?.length ? (
            <div className="rounded-2xl border border-fuchsia-200 bg-fuchsia-50/60 p-3">
              <p className="mb-2 text-xs font-bold uppercase tracking-wide text-fuchsia-600">Proposed changes ({plan.operations.length})</p>
              <div className="space-y-1.5">
                {plan.operations.map((op, i) => {
                  const { label, detail } = opSummary(op);
                  return (
                    <div key={i} className="flex items-start gap-2 text-xs">
                      <span className="mt-0.5 rounded-md bg-white px-1.5 py-0.5 font-semibold text-fuchsia-700">{label}</span>
                      {detail ? <span className="text-black/60">{detail}</span> : null}
                    </div>
                  );
                })}
              </div>
              <button onClick={applyPlan} disabled={applying} data-testid="admin-ai-apply" className="mt-3 inline-flex w-full items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-violet-500 to-fuchsia-500 py-2.5 text-sm font-bold text-white disabled:opacity-60">
                {applying ? <Loader2 size={15} className="animate-spin" /> : <Check size={15} />} Apply changes
              </button>
            </div>
          ) : null}

          {busy && (
            <div className="flex justify-start">
              <div className="flex items-center gap-2 rounded-2xl bg-black/[0.05] px-3.5 py-2.5 text-sm text-black/50"><Loader2 size={14} className="animate-spin" /> Thinking…</div>
            </div>
          )}
        </div>

        <div className="border-t border-black/10 p-3">
          {images.length > 0 && (
            <div className="mb-2 flex flex-wrap gap-2">
              {images.map((im, i) => (
                <div key={i} className="relative">
                  <img src={`${BACKEND_URL}${im.url}`} alt="" className="h-12 w-12 rounded-lg object-cover" />
                  <button onClick={() => setImages((prev) => prev.filter((_, idx) => idx !== i))} className="absolute -right-1.5 -top-1.5 rounded-full bg-red-600 p-0.5 text-white"><X size={11} /></button>
                </div>
              ))}
            </div>
          )}
          <div className="flex items-end gap-2 rounded-2xl border border-black/10 bg-black/[0.02] p-2">
            <button onClick={() => fileRef.current?.click()} data-testid="admin-ai-attach" className="shrink-0 rounded-lg p-2 text-black/40 hover:bg-black/5 hover:text-black" title="Attach images (or drag & drop)">
              {uploading ? <Loader2 size={18} className="animate-spin" /> : <ImagePlus size={18} />}
            </button>
            {voice.supported && (
              <button
                onClick={toggleVoice}
                data-testid="admin-ai-mic"
                title={voice.listening ? "Stop recording" : "Speak your prompt"}
                className={`relative shrink-0 rounded-lg p-2 transition-colors ${voice.listening ? "bg-red-500 text-white" : "text-black/40 hover:bg-black/5 hover:text-black"}`}
              >
                {voice.listening ? <MicOff size={18} /> : <Mic size={18} />}
                {voice.listening && <span className="absolute inset-0 -z-10 animate-ping rounded-lg bg-red-500/40" />}
              </button>
            )}
            <input ref={fileRef} type="file" accept="image/*" multiple className="hidden" onChange={attach} />
            <textarea
              value={prompt}
              onChange={(e) => { setPrompt(e.target.value); voiceBaseRef.current = e.target.value; }}
              onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); if (voice.listening) voice.stop(); send(); } }}
              rows={1}
              placeholder={voice.listening ? "Listening… tap the mic again when done" : "Describe a change to your site…"}
              data-testid="admin-ai-prompt"
              className="max-h-28 min-h-[24px] flex-1 resize-none bg-transparent py-1.5 text-sm outline-none placeholder:text-black/30"
            />
            <button onClick={() => { if (voice.listening) voice.stop(); send(); }} disabled={busy || !prompt.trim()} data-testid="admin-ai-send" className="shrink-0 rounded-lg bg-gradient-to-r from-violet-500 to-fuchsia-500 p-2 text-white disabled:opacity-40">
              <Send size={16} />
            </button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
